// Keep the header available when scrolling back up, without moving page content.
(() => {
  let header, section, observer;
  let lastY = 0, direction = 0, distance = 0, frame = 0;
  function update() {
    frame = 0;
    if (!header?.isConnected) return;
    const y = Math.max(0, Math.min(window.scrollY, document.documentElement.scrollHeight - window.innerHeight));
    const delta = y - lastY;
    const nextDirection = Math.sign(delta);
    if (nextDirection && nextDirection !== direction) distance = 0;
    if (nextDirection) direction = nextDirection;
    distance += Math.abs(delta);
    lastY = y;
    const top = section.getBoundingClientRect().top + window.scrollY;
    const height = header.offsetHeight;
    const pinned = y > top;
    header.classList.toggle('np-scroll-pinned', pinned);
    section.classList.toggle('np-scroll-header-is-pinned', pinned);
    const interacting = header.classList.contains('np-mobile-drawer-open') || header.querySelector('details[open], :focus-visible');
    if (!pinned || y <= top + height || interacting) {
      header.classList.remove('np-scroll-hidden');
    } else if (distance >= 8) {
      header.classList.toggle('np-scroll-hidden', direction > 0);
    }
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(update); }
  function init() {
    observer?.disconnect();
    header?.classList.remove('np-scroll-pinned', 'np-scroll-hidden');
    section?.classList.remove('np-scroll-header-space', 'np-scroll-header-is-pinned');
    header = document.querySelector('.np-header');
    section = header?.closest('.shopify-section');
    if (!section) return;
    section.classList.add('np-scroll-header-space');
    const measure = () => { section.style.setProperty('--np-header-height', Math.ceil(header.getBoundingClientRect().height) + 'px'); schedule(); };
    if ('ResizeObserver' in window) { observer = new ResizeObserver(measure); observer.observe(header); }
    measure();
    lastY = window.scrollY;
    distance = 0;
    direction = 0;
    header.classList.remove('np-scroll-hidden');
    schedule();
  }
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', () => {
    if (header && section) section.style.setProperty('--np-header-height', Math.ceil(header.getBoundingClientRect().height) + 'px');
    schedule();
  }, { passive: true });
  window.addEventListener('pageshow', init);
  document.addEventListener('focusin', event => { if (header?.contains(event.target)) header.classList.remove('np-scroll-hidden'); });
  document.addEventListener('toggle', event => { if (header?.contains(event.target)) schedule(); }, true);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(init));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();

/* NIPLUX motion: animated hover menus, one-time scroll reveals and subtle interactions. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const states = new WeakMap();
  const getState = menu => {
    if (!states.has(menu)) states.set(menu, { phase: menu.open ? 'open' : 'closed', token: 0, timer: null, animation: null });
    return states.get(menu);
  };
  const panelOf = menu => menu.querySelector(':scope > .np-mega, :scope > .np-submenu');
  const stopTimer = menu => { const state = getState(menu); clearTimeout(state.timer); state.timer = null; };
  const duration = ms => reduced.matches ? 0 : ms;
  function closeMenu(menu, immediate = false) {
    const state = getState(menu);
    stopTimer(menu);
    if (!menu.open || state.phase === 'closing') return;
    const panel = panelOf(menu);
    const token = ++state.token;
    const from = panel ? { opacity: getComputedStyle(panel).opacity, transform: getComputedStyle(panel).transform } : null;
    if (state.animation) state.animation.cancel();
    state.phase = 'closing';
    menu.classList.add('np-menu-closing');
    if (panel) panel.inert = true;
    const finish = () => {
      if (state.token !== token) return;
      menu.open = false;
      menu.classList.remove('np-menu-closing');
      if (state.animation) state.animation.cancel();
      state.animation = null;
      state.phase = 'closed';
    };
    if (!panel || immediate || reduced.matches || !panel.animate) { finish(); return; }
    state.animation = panel.animate([from, { opacity: 0, transform: 'translateY(-6px)' }], {
      duration: duration(180), easing: 'cubic-bezier(.4,0,1,1)', fill: 'both'
    });
    state.animation.finished.then(finish).catch(() => {});
  }
  function openMenu(menu) {
    const state = getState(menu);
    stopTimer(menu);
    if (menu.open && (state.phase === 'opening' || state.phase === 'open')) return;
    document.querySelectorAll('.np-menu[open]').forEach(other => { if (other !== menu) closeMenu(other); });
    const panel = panelOf(menu);
    const from = menu.open && panel ? { opacity: getComputedStyle(panel).opacity, transform: getComputedStyle(panel).transform } : { opacity: 0, transform: 'translateY(-10px)' };
    const token = ++state.token;
    if (state.animation) state.animation.cancel();
    state.phase = 'opening';
    menu.classList.remove('np-menu-closing');
    menu.open = true;
    if (menu.classList.contains('np-search-menu')) requestAnimationFrame(() => menu.querySelector('[data-np-search-input]')?.focus({ preventScroll: true }));
    if (panel) panel.inert = false;
    if (!panel || reduced.matches || !panel.animate) { state.phase = 'open'; state.animation = null; return; }
    state.animation = panel.animate([from, { opacity: 1, transform: 'translateY(0)' }], {
      duration: duration(320), easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both'
    });
    state.animation.finished.then(() => {
      if (state.token !== token) return;
      state.phase = 'open';
      state.animation.cancel();
      state.animation = null;
    }).catch(() => {});
  }
  document.addEventListener('pointerover', event => {
    if (window.matchMedia('(max-width: 749px)').matches || event.pointerType !== 'mouse' || !hover.matches || !(event.target instanceof Element)) return;
    const menu = event.target.closest('.np-menu');
    if (!menu || menu.classList.contains('np-search-menu')) return;
    stopTimer(menu);
    if (event.relatedTarget instanceof Node && menu.contains(event.relatedTarget)) return;
    openMenu(menu);
  });
  document.addEventListener('pointerout', event => {
    if (window.matchMedia('(max-width: 749px)').matches || event.pointerType !== 'mouse' || !hover.matches || !(event.target instanceof Element)) return;
    const menu = event.target.closest('.np-menu');
    if (!menu || menu.classList.contains('np-search-menu') || (event.relatedTarget instanceof Node && menu.contains(event.relatedTarget))) return;
    stopTimer(menu);
    getState(menu).timer = setTimeout(() => closeMenu(menu), 140);
  });
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const summary = event.target.closest('.np-menu > summary');
    if (summary) {
      event.preventDefault();
      const menu = summary.parentElement;
      if (menu.open && getState(menu).phase !== 'closing') closeMenu(menu); else openMenu(menu);
      return;
    }
    const close = event.target.closest('.np-menu-close');
    if (close) { const menu = close.closest('.np-menu'); closeMenu(menu); menu.querySelector('summary').focus(); return; }
    document.querySelectorAll('.np-menu[open]').forEach(menu => { if (!menu.contains(event.target)) closeMenu(menu); });
    const button = event.target.closest('[data-np-tab]');
    if (!button) return;
    const scope = button.closest('.shopify-section');
    if (!scope) return;
    scope.querySelectorAll('[data-np-tab]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    scope.querySelectorAll('.np-products').forEach(panel => {
      panel.hidden = panel.id !== button.dataset.npTab;
      if (!panel.hidden) {
        panel.querySelectorAll('.np-reveal').forEach(item => item.classList.add('np-revealed'));
        if (!reduced.matches && panel.animate) panel.animate([{ opacity: .2, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 280, easing: 'cubic-bezier(.22,1,.36,1)' });
      }
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const opened = Array.from(document.querySelectorAll('.np-menu[open]'));
    opened.forEach(menu => closeMenu(menu));
    if (opened.length) opened[opened.length - 1].querySelector('summary').focus();
  });
  document.addEventListener('toggle', event => {
    if (!(event.target instanceof Element) || !event.target.matches('.np-menu')) return;
    const menu = event.target;
    const state = getState(menu);
    if (!menu.open && state.phase !== 'closing') {
      ++state.token;
      if (state.animation) state.animation.cancel();
      state.phase = 'closed';
      menu.classList.remove('np-menu-closing');
    }
  }, true);
  let observer = null;
  function initReveal(root = document) {
    const selector = '.np-wrap .np-heading, .np-benefits > div, .np-products > .np-product, .np-care > a, .np-brand-photo, .np-copy, .np-lineup > article, .np-news > li, .np-media > figure, .np-wrap > .np-button, .topics-slider__item, [class^="topics-slider-"] > .page-width';
    const elements = root.querySelectorAll(selector);
    if (reduced.matches || !('IntersectionObserver' in window)) {
      elements.forEach(element => element.classList.add('np-revealed'));
      return;
    }
    if (!observer) observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('np-revealed');
        observer.unobserve(entry.target);
      });
    }, { threshold: .08, rootMargin: '0px 0px -24px 0px' });
    elements.forEach(element => {
      if (element.classList.contains('np-reveal')) return;
      element.classList.add('np-reveal');
      const siblings = Array.from(element.parentElement.children);
      element.style.setProperty('--np-reveal-delay', `${Math.min(siblings.indexOf(element), 3) * 65}ms`);
      observer.observe(element);
    });
  }
  const ready = () => initReveal();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true }); else ready();
  document.addEventListener('shopify:section:load', event => initReveal(event.target));
  document.addEventListener('shopify:section:select', event => {
    event.target.querySelectorAll('.np-reveal').forEach(element => element.classList.add('np-revealed'));
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.np-menu').forEach(menu => closeMenu(menu, true));
    event.target.querySelectorAll('.np-reveal').forEach(element => { if (observer) observer.unobserve(element); });
  });
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      if (observer) { observer.disconnect(); observer = null; }
      document.querySelectorAll('.np-reveal').forEach(element => element.classList.add('np-revealed'));
      document.querySelectorAll('.np-menu[open]').forEach(menu => {
        const state = getState(menu);
        if (state.animation) state.animation.finish();
      });
    } else initReveal();
  });
})();

// Japanese-compatible suggestions using the Shopify section rendering endpoint.
(() => {
  const states = new WeakMap();
  const stateFor = panel => {
    if (!states.has(panel)) states.set(panel, { timer: null, controller: null, serial: 0, composing: false });
    return states.get(panel);
  };
  async function search(panel) {
    const state = stateFor(panel);
    clearTimeout(state.timer);
    const input = panel.querySelector('[data-np-search-input]');
    const query = input.value.trim();
    const list = panel.querySelector('[data-np-search-results]');
    const status = panel.querySelector('[data-np-search-status]');
    const serial = ++state.serial;
    if (state.controller) state.controller.abort();
    list.replaceChildren();
    if (!query) { status.textContent = ''; list.removeAttribute('aria-busy'); return; }
    state.controller = new AbortController();
    status.textContent = '検索しています…';
    list.setAttribute('aria-busy', 'true');
    try {
      const endpoint = new URL(panel.dataset.npSearchEndpoint, window.location.origin);
      endpoint.search = new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last', section_id: 'np-search-results' }).toString();
      const response = await fetch(endpoint, { signal: state.controller.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Search unavailable');
      const html = await response.text();
      if (serial !== state.serial) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const responseList = documentFragment.querySelector('[data-np-search-response]');
      if (!responseList) throw new Error('Search section missing');
      // Rebuild only product links/images/text; never insert response scripts.
      Array.from(responseList.children).forEach(product => {
        const sourceLink = product.querySelector('a');
        if (!sourceLink) return;
        const url = new URL(sourceLink.getAttribute('href'), window.location.origin);
        if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return;
        const item = document.createElement('li');
        const link = document.createElement('a'); link.href = url.href;
        const sourceImage = sourceLink.querySelector('img');
        if (sourceImage) {
          const src = new URL(sourceImage.getAttribute('src'), window.location.origin);
          if (['http:', 'https:'].includes(src.protocol)) {
            const image = document.createElement('img'); image.src = src.href; image.alt = ''; image.width = 64; image.height = 64; image.loading = 'lazy'; link.append(image);
          }
        }
        const title = document.createElement('span'); title.textContent = sourceLink.querySelector('span')?.textContent || sourceLink.textContent.trim(); link.append(title);
        item.append(link); list.append(item);
      });
      status.textContent = list.children.length ? `「${query}」の商品候補 ${list.children.length}件` : '候補が見つかりません。検索ボタンで商品一覧を確認できます。';
    } catch (error) {
      if (error.name !== 'AbortError' && serial === state.serial) status.textContent = '候補を表示できません。検索ボタンから検索してください。';
    } finally { if (serial === state.serial) list.removeAttribute('aria-busy'); }
  }
  document.addEventListener('input', event => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-np-search-input]')) return;
    const panel = event.target.closest('[data-np-search-panel]'); const state = stateFor(panel);
    clearTimeout(state.timer); ++state.serial; if (state.controller) state.controller.abort();
    if (!state.composing && !event.isComposing) state.timer = setTimeout(() => search(panel), 220);
  });
  document.addEventListener('compositionstart', event => { if (event.target.matches?.('[data-np-search-input]')) stateFor(event.target.closest('[data-np-search-panel]')).composing = true; });
  document.addEventListener('compositionend', event => { if (event.target.matches?.('[data-np-search-input]')) { const panel = event.target.closest('[data-np-search-panel]'); stateFor(panel).composing = false; search(panel); } });
  document.addEventListener('click', event => {
    const chip = event.target.closest?.('[data-np-search-keyword]');
    if (!chip) return;
    const panel = chip.closest('[data-np-search-panel]');
    const input = panel.querySelector('[data-np-search-input]');
    input.value = chip.dataset.npSearchKeyword; input.focus({ preventScroll: true }); search(panel);
  });
  document.addEventListener('submit', event => {
    if (!event.target.matches?.('.np-search-form')) return;
    const input = event.target.querySelector('[data-np-search-input]'); input.value = input.value.trim();
    if (!input.value) { event.preventDefault(); input.focus(); }
  });
})();

// Mobile full-screen navigation; desktop keeps its existing hover menus.
(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  let current = null;
  let returnFocus = null;
  let savedOverflow = '';
  function closeDrawer(focus = true) {
    if (!current) return;
    const header = current;
    const nav = header.querySelector('[data-np-mobile-nav]');
    header.classList.remove('np-mobile-drawer-open');
    header.querySelector('[data-np-mobile-toggle]').setAttribute('aria-expanded', 'false');
    nav.inert = mobile.matches;
    nav.removeAttribute('aria-modal');
    nav.removeAttribute('role');
    if (mobile.matches) nav.setAttribute('aria-hidden', 'true'); else nav.removeAttribute('aria-hidden');
    nav.querySelectorAll('.np-menu[open]').forEach(menu => menu.open = false);
    document.body.style.overflow = savedOverflow;
    current = null;
    if (focus && mobile.matches && returnFocus?.isConnected) returnFocus.focus();
  }
  function openDrawer(header, button) {
    if (!mobile.matches) return;
    if (current) closeDrawer(false);
    returnFocus = button;
    // Close search/desktop popovers before opening mobile navigation.
    document.querySelectorAll('.np-menu[open]').forEach(menu => menu.open = false);
    const nav = header.querySelector('[data-np-mobile-nav]');
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    nav.inert = false;
    nav.removeAttribute('aria-hidden');
    nav.setAttribute('role', 'dialog');
    nav.setAttribute('aria-modal', 'true');
    header.classList.add('np-mobile-drawer-open');
    button.setAttribute('aria-expanded', 'true');
    current = header;
    nav.querySelector('[data-np-mobile-close]').focus();
  }
  function init(root = document) {
    const headers = root.matches?.('.np-header') ? [root] : root.querySelectorAll('.np-header');
    headers.forEach(header => {
      header.classList.add('np-mobile-ready');
      const nav = header.querySelector('[data-np-mobile-nav]');
      if (!nav) return;
      nav.inert = mobile.matches && !header.classList.contains('np-mobile-drawer-open');
      if (mobile.matches && !header.classList.contains('np-mobile-drawer-open')) nav.setAttribute('aria-hidden', 'true'); else nav.removeAttribute('aria-hidden');
    });
  }
  document.addEventListener('click', event => {
    const toggle = event.target.closest?.('[data-np-mobile-toggle]');
    if (toggle) {
      const header = toggle.closest('.np-header');
      if (current === header) closeDrawer(); else openDrawer(header, toggle);
      return;
    }
    if (event.target.closest?.('[data-np-mobile-close]')) { closeDrawer(); return; }
    if (current && event.target.closest?.('[data-np-mobile-nav] a[href]')) closeDrawer(false);
  });
  document.addEventListener('keydown', event => {
    if (!current) return;
    if (event.key === 'Escape') { closeDrawer(); return; }
    if (event.key !== 'Tab') return;
    const nav = current.querySelector('[data-np-mobile-nav]');
    const focusable = Array.from(nav.querySelectorAll('a[href], button:not([disabled]), summary, input:not([disabled]), [tabindex="0"]')).filter(element => element.getClientRects().length && getComputedStyle(element).visibility === 'visible' && !element.closest('[inert]'));
    if (!focusable.length) { event.preventDefault(); return; }
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  mobile.addEventListener('change', () => { closeDrawer(false); init(); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => { if (current && event.target.contains(current)) closeDrawer(false); });
})();

// Fit the homepage hero and following NEWS bar into the initial viewport.
(() => {
  let resizeObserver = null;
  let frame = 0;
  let heroRoot = null;
  let newsBar = null;
  let lastHeight = null;
  function measure() {
    frame = 0;
    if (!heroRoot?.isConnected) return;
    const active = document.activeElement;
    // Avoid changing the hero when a phone's software keyboard is open.
    if (window.innerWidth <= 749 && active?.matches('input, textarea')) return;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const top = heroRoot.getBoundingClientRect().top + window.scrollY;
    const newsHeight = newsBar ? newsBar.getBoundingClientRect().height : 0;
    const height = Math.max(0, Math.floor(viewportHeight - top - newsHeight - 2));
    if (height === lastHeight) return;
    lastHeight = height;
    heroRoot.style.setProperty('--np-hero-height', `${height}px`);
    const hero = heroRoot.querySelector('.hero');
    const slideshow = window.Flickity?.data?.(hero);
    if (slideshow) slideshow.resize();
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(measure); }
  function init() {
    if (!document.body.classList.contains('template-index')) return;
    heroRoot = document.querySelector('#MainContent [data-section-type="slideshow-section"]');
    if (!heroRoot) return;
    const hero = heroRoot.querySelector('.hero');
    if (!hero) return;
    heroRoot.classList.add('np-first-screen');
    hero.parentElement.classList.add('np-first-screen-frame');
    const wrapper = heroRoot.closest('.shopify-section');
    newsBar = wrapper?.nextElementSibling?.querySelector('[class^="action-bar-"]') || null;
    lastHeight = null;
    if (resizeObserver) resizeObserver.disconnect();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(schedule);
      document.querySelectorAll('.np-header, .shopify-section-group-header-group').forEach(el => resizeObserver.observe(el));
      if (newsBar) resizeObserver.observe(newsBar);
    }
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });
  window.addEventListener('load', schedule, { once: true });
  document.fonts?.ready.then(schedule);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();

// TOPICS carousel, reinitializable from the Shopify theme editor.
(() => {
  const instances = new WeakMap();
  function init(root = document) {
    const sections = root.matches?.('[data-np-topics-carousel]') ? [root] : root.querySelectorAll('[data-np-topics-carousel]');
    sections.forEach(section => {
      if (instances.has(section)) return;
      const track = section.querySelector('[data-np-topics-track]');
      const prev = section.querySelector('[data-np-topics-prev]');
      const next = section.querySelector('[data-np-topics-next]');
      const progress = section.querySelector('[data-np-topics-progress]');
      if (!track) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      let timer = null;
      let paused = false;
      const controller = new AbortController();
      const on = (node, event, callback) => node?.addEventListener(event, callback, { signal: controller.signal });
      const update = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max - 1;
        if (progress) {
          const fraction = Math.min(1, track.clientWidth / Math.max(1, track.scrollWidth));
          progress.style.width = `${fraction * 100}%`;
          progress.style.left = `${max ? (track.scrollLeft / max) * (1 - fraction) * 100 : 0}%`;
        }
      };
      const step = () => { const item = track.querySelector('.topics-slider__item'); return item ? item.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0) : track.clientWidth; };
      const move = direction => track.scrollBy({ left: direction * step(), behavior: reduced.matches ? 'auto' : 'smooth' });
      const stop = () => { clearInterval(timer); timer = null; };
      const start = () => {
        stop();
        if (section.dataset.autoplay !== 'true' || reduced.matches || paused || track.scrollWidth <= track.clientWidth + 1) return;
        timer = setInterval(() => {
          if (document.hidden) return;
          if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 1) track.scrollTo({ left: 0, behavior: 'smooth' }); else move(1);
        }, 5000);
      };
      on(prev, 'click', () => { move(-1); start(); });
      on(next, 'click', () => { move(1); start(); });
      on(track, 'scroll', update);
      on(section, 'pointerenter', () => { paused = true; stop(); });
      on(section, 'pointerleave', () => { paused = section.contains(document.activeElement); start(); });
      on(section, 'focusin', () => { paused = true; stop(); });
      on(section, 'focusout', () => requestAnimationFrame(() => { paused = section.contains(document.activeElement); start(); }));
      on(track, 'keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      on(reduced, 'change', () => { update(); start(); });
      const observer = new ResizeObserver(() => { update(); start(); });
      observer.observe(track);
      instances.set(section, () => { stop(); observer.disconnect(); controller.abort(); instances.delete(section); });
      update(); start();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-np-topics-carousel]').forEach(section => instances.get(section)?.()));
})();

// Approved plural metafield keys; old menu settings are translated only in this theme.
(() => {
  function normalizeFilterUrl(href, base) {
    const url = new URL(href, base);
    if (url.origin !== new URL(base).origin) return url;
    const aliases = [['filter.p.m.custom.Item','filter.p.m.custom.items'],['filter.p.m.custom.item','filter.p.m.custom.items'],['filter.p.m.custom.category','filter.p.m.custom.categories']];
    aliases.forEach(([oldKey,newKey]) => {
      const values = [...url.searchParams.getAll(newKey), ...url.searchParams.getAll(oldKey)];
      if (!url.searchParams.has(oldKey)) return;
      url.searchParams.delete(oldKey);url.searchParams.delete(newKey);
      [...new Set(values)].forEach(value => url.searchParams.append(newKey,value));
    });
    return url;
  }
  function syncFilterLinks() {
    document.querySelectorAll('a[href*="filter.p.m.custom."]').forEach(link => {
      const url = normalizeFilterUrl(link.href, location.href);
      if (url.href !== link.href) link.href = url.href;
    });
  }
  const current = normalizeFilterUrl(location.href, location.href);
  if (current.href !== location.href) { location.replace(current.href); return; }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',syncFilterLinks); else syncFilterLinks();
  document.addEventListener('shopify:section:load',syncFilterLinks);
  document.addEventListener('click',event => {
    const link = event.target.closest?.('a[href]');
    if (link) { const url = normalizeFilterUrl(link.href,location.href); if (url.href !== link.href) link.href = url.href; }
  },true);
})();

// Category search chooses one value; products can still belong to many categories.
(function(){
  const keys=['filter.p.m.custom.categories','filter.p.m.custom.category'];
  const itemKeys=['filter.p.m.custom.items','filter.p.m.custom.item','filter.p.m.custom.Item'];
  function clearCategories(params){keys.forEach(key=>params.delete(key));}
  // Capture before Impulse serializes the sidebar/drawer form on the input event.
  document.addEventListener('input',event=>{
    const input=event.target;
    if(!(input instanceof HTMLInputElement)||!input.closest('.filter-form'))return;
    const group=keys.includes(input.name)?keys:itemKeys.includes(input.name)?itemKeys:null;
    if(!group)return;
    document.querySelectorAll('.filter-form input').forEach(other=>{
      if(group.includes(other.name))other.checked=other.name===input.name&&other.value===input.value&&input.checked;
    });
  },true);
  function init(){
    const nav=document.querySelector('[data-np-category-navigation]');
    const current=new URL(window.location.href);
    const key=keys[0];
    const itemValues=itemKeys.flatMap(name=>current.searchParams.getAll(name));
    if(itemValues.length>1){
      itemKeys.forEach(name=>current.searchParams.delete(name));
      current.searchParams.set(itemKeys[0],itemValues[itemValues.length-1]);
      current.searchParams.delete('page');
      window.location.replace(current.href);
      return;
    }
    const values=keys.flatMap(name=>current.searchParams.getAll(name));
    // Existing bookmarked multi-category URLs are narrowed to their last choice.
    if(values.length>1){
      const preferred=current.searchParams.getAll(key);
      const value=preferred.length?preferred[preferred.length-1]:values[values.length-1];
      clearCategories(current.searchParams);
      current.searchParams.append(key,value);
      current.searchParams.delete('page');
      window.location.replace(current.href);
      return;
    }
    const itemNav=document.querySelector('[data-np-item-navigation]');
    if((!nav&&!itemNav)||(nav?.dataset.npCategoryReady||itemNav?.dataset.npItemReady))return;
    if(nav)nav.dataset.npCategoryReady='true';
    if(itemNav)itemNav.dataset.npItemReady='true';
    function sync(){
      const current=new URL(window.location.href);
      const selected=current.searchParams.getAll(key);
      nav?.querySelectorAll('[data-np-category-value]').forEach(link=>{
        const value=link.dataset.npCategoryValue;
        const active=selected.includes(value);
        const target=new URL(current.href);
        clearCategories(target.searchParams);
        if(!active)target.searchParams.append(key,value);
        target.searchParams.delete('page');
        target.hash='';
        link.href=target.pathname+target.search;
        link.classList.toggle('is-active',active);
      });
      if(itemNav){
        const itemKey=itemNav.dataset.npItemKey;
        const chosen=current.searchParams.getAll(itemKey);
        itemNav.querySelectorAll('[data-np-item-value]').forEach(link=>{
          const value=link.dataset.npItemValue;
          const active=value ? chosen.includes(value) : chosen.length===0;
          const target=new URL(current.href);
          itemKeys.forEach(name=>target.searchParams.delete(name));
          if(value&&!active)target.searchParams.append(itemKey,value);
          target.searchParams.delete('page');target.hash='';
          link.href=target.pathname+target.search;
          link.classList.toggle('is-active',active);
        });
      }
    }
    const originalSync=sync;
    function syncReset(){
      const target=new URL(window.location.href);
      [...target.searchParams.keys()].forEach(name=>{if(name.startsWith('filter.'))target.searchParams.delete(name);});
      target.searchParams.delete('page');target.hash='';
      document.querySelectorAll('[data-np-filter-reset]').forEach(link=>{link.href=target.pathname+target.search;});
    }
    sync=function(){originalSync();syncReset();};
    sync();
    window.addEventListener('popstate',sync);
    const content=document.getElementById('CollectionAjaxContent');
    if(content){const observer=new MutationObserver(sync);observer.observe(content,{childList:true,subtree:true});}
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();
  document.addEventListener('shopify:section:load',init);
})();

// Align every visible collection card after filtering, resizing, or font loading.
(function(){
  if(!document.body.classList.contains('template-collection'))return;
  if(document.querySelector('.np-collection-card-layout'))return;
  let pending=0;
  function align(){
    pending=0;
    const cards=Array.from(document.querySelectorAll('.collection-grid__wrapper>.grid>.grid-product .grid-product__content')).filter(card=>card.getBoundingClientRect().width>0);
    if(!cards.length)return;
    const fields=['.np-variant-thumbnails','.np-products-card-tags','.grid-product__title','.np-products-card-summary','.grid-product__price'];
    cards.forEach(card=>{card.style.minHeight='';fields.forEach(selector=>{const node=card.querySelector(selector);if(node)node.style.minHeight='';});});
    fields.forEach(selector=>{
      const nodes=cards.map(card=>card.querySelector(selector)).filter(Boolean);
      const height=Math.ceil(Math.max(0,...nodes.map(node=>node.getBoundingClientRect().height)));
      nodes.forEach(node=>node.style.minHeight=height+'px');
    });
    const height=Math.ceil(Math.max(...cards.map(card=>card.getBoundingClientRect().height)));
    cards.forEach(card=>card.style.minHeight=height+'px');
  }
  function schedule(){if(!pending)pending=requestAnimationFrame(align);}
  window.addEventListener('resize',schedule);
  document.addEventListener('shopify:section:load',schedule);
  if(document.fonts)document.fonts.ready.then(schedule);
  const main=document.getElementById('MainContent');
  if(main){const observer=new MutationObserver(schedule);observer.observe(main,{childList:true,subtree:true});}
  schedule();
})();

// Preview variant photos on hover/focus; native thumbnail links still open the variant.
(function(){
  const states=new WeakMap();
  function getState(card){
    let state=states.get(card);
    if(state)return state;
    const mask=card.querySelector('.grid-product__image-mask');
    const link=card.querySelector('a.np-products-card-detail, a.grid-product__link');
    if(!mask||!link)return null;
    const image=document.createElement('img');
    image.className='np-variant-preview-image';image.alt='';image.decoding='async';
    image.setAttribute('aria-hidden','true');
    mask.appendChild(image);
    state={image,link,originalHref:link.getAttribute('href'),token:0};
    states.set(card,state);return state;
  }
  function reset(card){
    const state=states.get(card);
    if(!state)return;
    state.token++;
    state.image.classList.remove('is-visible');
    state.link.setAttribute('href',state.originalHref);
    card.classList.remove('np-variant-previewing');
    card.querySelectorAll('.np-variant-thumb').forEach(thumb=>thumb.classList.remove('is-previewing'));
  }
  function preview(thumb){
    const card=thumb.closest('.grid-product__content');
    if(!card)return;
    const src=thumb.dataset.npVariantImage;
    if(!src){reset(card);return;}
    const state=getState(card);if(!state)return;
    const token=++state.token;
    const preload=new Image();
    preload.onload=()=>{
      if(token!==state.token||!card.isConnected)return;
      state.image.src=src;
      state.image.classList.add('is-visible');
      state.link.setAttribute('href',thumb.dataset.npVariantUrl || thumb.getAttribute('href'));
      card.classList.add('np-variant-previewing');
      card.querySelectorAll('.np-variant-thumb').forEach(other=>other.classList.toggle('is-previewing',other===thumb));
    };
    preload.onerror=()=>{if(token===state.token)reset(card);};
    preload.src=src;
  }
  document.addEventListener('click',event=>{
    const thumb=event.target.closest('button.np-variant-thumb');
    if(thumb)preview(thumb);
  });
  document.addEventListener('pointerover',event=>{
    if(event.pointerType==='touch')return;
    const thumb=event.target.closest('.np-variant-thumb');
    if(thumb&&!thumb.contains(event.relatedTarget))preview(thumb);
  });
  document.addEventListener('pointerout',event=>{
    if(event.pointerType==='touch')return;
    const card=event.target.closest('.grid-product__content');
    if(card&&!card.contains(event.relatedTarget))reset(card);
  });
  document.addEventListener('focusin',event=>{
    const thumb=event.target.closest('.np-variant-thumb');if(thumb)preview(thumb);
  });
  document.addEventListener('focusout',event=>{
    const card=event.target.closest('.grid-product__content');
    if(card&&!card.contains(event.relatedTarget))reset(card);
  });
})();

// Smooth content accordions; navigation menus keep their own interaction.
(() => {
  if (window.nipluxSmoothAccordions) return;
  window.nipluxSmoothAccordions = true;
  const states = new WeakMap();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.addEventListener('click', event => {
    const summary = event.target.closest('summary');
    if (!summary || event.defaultPrevented || event.button > 0) return;
    const details = summary.parentElement;
    if (details.tagName !== 'DETAILS' || details.closest('header, nav, .np-menu, np-image-variant, [data-no-accordion-animation]')) return;
    if (event.target.closest('a, button, input, select, textarea')) return;
    if (reduced.matches || !details.animate) return;
    event.preventDefault();
    let state = states.get(details);
    const start = details.getBoundingClientRect().height;
    const expand = state ? !state.expand : !details.open;
    if (state) state.animation.cancel();
    const overflow = state ? state.overflow : details.style.overflow;
    details.open = true;
    const end = expand ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height + parseFloat(getComputedStyle(details).borderTopWidth || 0) + parseFloat(getComputedStyle(details).borderBottomWidth || 0);
    details.style.overflow = 'hidden';
    const animation = details.animate([{ height: `${start}px` }, { height: `${end}px` }], {
      duration: 300, easing: 'cubic-bezier(.22,1,.36,1)'
    });
    state = { animation, expand, overflow };
    states.set(details, state);
    animation.onfinish = () => {
      if (states.get(details) !== state) return;
      details.open = expand;
      details.style.overflow = overflow;
      states.delete(details);
    };
  });
})();
